import { useContext } from 'react';

import { useAppSelector, useAppDispatch } from 'hooks';
import { useFeatureFlag } from 'hooks/useFeatureFlag';

import { Text, SimpleGrid } from '@chakra-ui/react';

import Card from 'components/card/Card';

import FormInput from 'components/fields/auto/FormInput';
import RadioInput from 'components/fields/auto/RadioInput';
import getRadioYesNo from 'helpers/getRadioYesNo';
import requiresPropertyBinderNumber from 'helpers/requiresPropertyBinderNumber';

import { ApplicationFormContext } from 'contexts/ApplicationFormContext';

import {
  changePolicyInformation,
  changeLanguage,
  changeReportOfBrokerAgent,
  changeCompanyAndBrokerInformation,
  removeMissingMandatoryField,
} from 'reducers/autoPersonalFormReducer';

import { SECTIONS } from 'views/auto_form/constants';

import PropertyBinderNumberField from './components/PropertyBinderNumberField';

const PolicyInformation = ({ uploadLOA }: { uploadLOA?: boolean | null }) => {
  const isPersonalLOAEnabled = useFeatureFlag('enable-loa-personal-lines');
  const dispatch = useAppDispatch();

  const { validations } = useContext(ApplicationFormContext) as {
    validations: Validation[];
  };

  const requiresBinder = requiresPropertyBinderNumber(validations);

  const data = useAppSelector(
    (state) => state.autoPersonalForm.data.policy_information,
  );

  const companyAndBroker = useAppSelector(
    (state) => state.autoPersonalForm.data.company_and_broker_information,
  );

  const onPolicyInformationChange = (event: any) => {
    const { name, value, checked, type } = event.target;
    const payload = type === 'checkbox' ? checked : value;

    dispatch(changePolicyInformation({ name, value: payload }));
  };

  const onCompanyAndBrokerInformationChange = (event: any) => {
    const { name, value } = event.target;

    dispatch(changeCompanyAndBrokerInformation({ name, value }));
  };

  const onStatusToggle = (status: string) => {
    const isNewPolicy = status === 'new';

    dispatch(
      changePolicyInformation({ name: 'new_policy', value: isNewPolicy }),
    );
    dispatch(
      changePolicyInformation({ name: 'is_remarket', value: !isNewPolicy }),
    );
    dispatch(
      changeReportOfBrokerAgent({
        name: 'is_this_business_new_to_you',
        value: isNewPolicy,
      }),
    );

    if (isNewPolicy) {
      dispatch(removeMissingMandatoryField('Customer Code'));
    }
  };

  const getStatusValue = () => {
    if (data.new_policy) return 'new';
    if (data.is_remarket) return 'remarket';

    return null;
  };

  const onLOAChange = (value: string) => {
    dispatch(
      changePolicyInformation({ name: 'is_loa', value: value === 'true' }),
    );
  };

  return (
    <Card mb="20px" id="sectionPolicyInformation">
      <Text fontWeight="bold" fontSize="2xl" mb="20px">
        Policy Information
      </Text>

      <SimpleGrid columns={4} spacing="20px" mb="20px">
        <FormInput
          section={SECTIONS.POLICY_INFORMATION}
          label="Applied Quote #"
          name="policy_no_assigned"
          value={data.policy_no_assigned}
          onChange={onPolicyInformationChange}
          required
        />
        <FormInput
          section={SECTIONS.POLICY_INFORMATION}
          label="Replacing Policy/Binder #"
          name="replacing_policy_no"
          value={data.replacing_policy_no}
          onChange={onPolicyInformationChange}
        />
        <RadioInput
          label="Is this a new policy?"
          section={SECTIONS.POLICY_INFORMATION}
          options={[
            { value: 'new', label: 'New Business' },
            { value: 'remarket', label: 'Remarket' },
          ]}
          value={getStatusValue()}
          onChange={onStatusToggle}
          required
          stacked
        />
        <RadioInput
          section={SECTIONS.POLICY_INFORMATION}
          label="Language"
          options={[
            { value: 'en', label: 'English' },
            { value: 'fr', label: 'French' },
          ]}
          value={data.language_preferred}
          onChange={(value: any) => dispatch(changeLanguage(value))}
          stacked
          disabled
        />
        <RadioInput
          section={SECTIONS.POLICY_INFORMATION}
          label="Is this an LOA?"
          options={[
            { value: 'true', label: 'Yes' },
            { value: 'false', label: 'No' },
          ]}
          value={getRadioYesNo(data.is_loa)}
          onChange={onLOAChange}
          disabled={isPersonalLOAEnabled && uploadLOA != null}
          stacked
          required
        />
        <FormInput
          section={SECTIONS.POLICY_INFORMATION}
          label="Customer Code"
          name="customer_code"
          value={data.customer_code}
          onChange={onPolicyInformationChange}
          required={data.is_remarket}
        />
        <FormInput
          section={SECTIONS.INSURANCE_COMPANY_INFORMATION}
          label="CSR Code"
          name="broker_csr_code"
          value={companyAndBroker.broker_csr_code || ''}
          onChange={onCompanyAndBrokerInformationChange}
          required
        />
        {requiresBinder && (
          <PropertyBinderNumberField validations={validations} />
        )}
      </SimpleGrid>
    </Card>
  );
};

export default PolicyInformation;
